import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, forkJoin, from, map, merge, Observable, of, shareReplay, switchMap } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { NewBonusWalletApi } from 'src/app/shared/apis/new-bonus-wallet.api';
import { SocialData } from 'src/app/shared/interfaces/account.interface';
import { NewViewData, WalletListData } from 'src/app/shared/interfaces/funds.interface';
import {
  AllRateData,
  CurrencyBalance,
  TransferwWallet,
  WalletViewData,
} from 'src/app/shared/interfaces/wallet.interface';
import { CurrencyValuePipe } from 'src/app/shared/pipes/currency-value.pipe';
import { signalrMessage } from 'src/app/shared/service/signalr.service';
import { UserAssetsService } from '../../user-asset/user-assets.service';
import * as Config from './funds.config';

type WalletDesc = Array<{ type: string; desc: string }>;

@Injectable({ providedIn: 'root' })
export class FundsService {
  constructor(
    private appService: AppService,
    private userAssetsService: UserAssetsService,
    private currencyValuePipe: CurrencyValuePipe,
    private newBonusWalletApi: NewBonusWalletApi,
  ) {}

  private readonly manualWalletSummaryRefresh$ = new BehaviorSubject<void>(undefined);
  private readonly userBalanceRefresh$ = this.appService.userBalance$.pipe(
    filter(Boolean),
    map(() => undefined),
  );

  /**
   * 保持原邏輯：組 walletList（含主錢包 desc、遊戲錢包 desc）、再批次補抓遊戲錢包幣別餘額與 totalBalance。
   * 不再觸碰 component；回傳 Observable<NewViewData> 讓 component 訂閱後自行 set 狀態。
   *
   * @param walletViewData
   * @param walletDesc
   * @returns Observable<NewViewData>
   */
  formatAndProcessWalletViewData(walletViewData: WalletViewData, walletDesc: WalletDesc): Observable<NewViewData> {
    const { totalAsset } = walletViewData;

    // 1) 建立 walletList（主錢包 + 遊戲錢包）— desc 來源維持用 walletDesc
    const walletList: WalletListData[] = [];

    // 主錢包
    walletList.push({
      ...walletViewData.overviewWallet,
      currency: 'USDT',
      isMain: true,
      desc: walletDesc.find((x: any) => x.type === 'Main')?.desc || '',
    });

    // 遊戲錢包
    walletViewData.transferWallet.forEach((game: TransferwWallet) => {
      const currency = game.isFirst ? 'USDT' : game.currencies.length > 1 ? 'USDT' : game.currencies[0].currency;

      walletList.push({
        ...game,
        currency,
        isMain: false,
        desc: walletDesc.find((x: any) => x.type === game.category)?.desc || '',
        totalBalance: 0,
      });
    });

    // 2) 準備補抓餘額的 observable 陣列（保持你原本的邏輯：isFirst → of(null)，否則 forkJoin 幣別）
    const getList = walletList.map(item => {
      if (item.category !== 'Main') {
        if (item.isFirst) {
          return of(null);
        } else {
          return forkJoin(
            item.currencies.map(x => this.userAssetsService.getTransferWalletBalance(item.providerId!, x.currency)),
          );
        }
      } else {
        return of(null);
      }
    });

    // 與你原本一致：有錢包需要更新餘額就 forkJoin(getList)；（其實就算都 of(null) 也能安全執行）
    return forkJoin(getList).pipe(
      map(allRes => {
        allRes.forEach((resArr, x) => {
          if (resArr) {
            // 塞回幣別餘額
            resArr.forEach((res, y) => {
              if (res) {
                walletList[x].currencies[y].balance = res.totalBalance;
              }
            });

            // 多幣別 → 以你原來的 a.add(b.totalBalance.subtract(b.rate)) 聚合
            if (resArr.length > 1) {
              walletList[x].totalBalance = resArr.reduce((a, b) => {
                return a.add(b ? b.totalBalance.subtract(b.rate) : 0);
              }, 0);
            } else {
              // 單幣別 → 直接取唯一幣別餘額
              walletList[x].totalBalance = walletList[x].currencies[0].balance!;
            }
          }
        });

        // 回傳給 component
        return {
          walletList,
          pubsWalletAsset: totalAsset,
          lockBonus: 0,
          totalAsset: 0,
        } as NewViewData;
      }),
    );
  }

  /**
   * 保持你原先在 component 中的計算語意：提款限額（USDT）
   *
   * @param walletInfo
   * @param allRate
   */
  calcWithdrawLimit(walletInfo: WalletViewData, allRate: AllRateData): number {
    const currencies = walletInfo.overviewWallet.currencies;
    return currencies.reduce((a, b) => {
      if (Number(b?.withdrawLimit || 0) <= 0) return a;
      const rate = allRate?.rates?.find(r => r?.currency === b?.currency)?.rate ?? 0;
      return Number(a || 0).add(Number(b?.withdrawLimit || 0).subtract(rate));
    }, 0);
  }

  /**
   * 總資產計算（維持你原本 reduce 的語意：只聚合遊戲錢包）
   * 備註：你原始程式是以 this.newViewData.totalAsset 當作 reduce 的初始值，
   *      在 component 那裡我會維持同樣寫法，避免改動你的結果。
   *
   * @param walletList
   * @param allRate
   */
  calcWalletListAssetPart(walletList: WalletListData[], allRate: AllRateData): number {
    return walletList.reduce((a, b) => {
      let total = 0;
      if (!b.isMain) {
        total =
          b.currencies.reduce((x, y) => {
            const rate = allRate?.rates?.find(r => r?.currency === y?.currency)?.rate ?? 0;
            return Number(x).add((y?.balance || 0).subtract(rate));
          }, 0) ?? 0;
      }
      return Number(a ?? 0).add(total);
    }, 0);
  }

  /**
   * 單幣更新（純資料變換，回傳新 balances 與可選的動畫字串）
   *
   * @param allCurrencyBalance
   * @param msg
   */
  updateSingleCurrency(
    allCurrencyBalance: CurrencyBalance[],
    msg: signalrMessage,
  ): { balances: CurrencyBalance[]; animationAmount?: string } {
    const balances = [...allCurrencyBalance];
    const item = balances.find(balanceItem => balanceItem.currency === msg.data?.Currency);
    if (!item) return { balances };

    if (msg?.type === 'NonStickyBalance') {
      if (msg?.data?.Category === 'SlotGame') {
        item.nsSlotGame!.balance = msg.data.Balance || 0;
      } else {
        item.nsLiveCasino!.balance = msg.data?.Balance || 0;
      }
    } else {
      item.balance = msg.data?.Balance;
    }

    item.nonStickyBalance = Number(item.balance)
      .add(Number(item?.nsSlotGame?.balance || 0))
      .add(Number(item?.nsLiveCasino?.balance || 0));

    let animationAmount: string | undefined;
    if (msg.data?.Amount > 0) {
      animationAmount = this.currencyValuePipe.transform(msg.data?.Amount, msg.data?.Currency);
    }

    return { balances, animationAmount };
  }

  /**
   * 多幣更新（純資料變換）
   *
   * @param allCurrencyBalance
   * @param msg
   */
  updateMultiCurrency(allCurrencyBalance: CurrencyBalance[], msg: signalrMessage): CurrencyBalance[] {
    const balances = [...allCurrencyBalance];
    const objBalance = msg.data?.Balance as Record<string, number> | undefined;

    Object.keys(objBalance || {}).forEach(key => {
      const itm = balances.find(balanceItem => balanceItem.currency === key);
      if (!itm) return;
      itm.balance = objBalance![key];
      itm.nonStickyBalance = Number(objBalance![key] || 0)
        .add(itm.nsSlotGame?.currency === key ? itm.nsSlotGame?.balance || 0 : 0)
        .add(itm.nsLiveCasino?.currency === key ? itm.nsLiveCasino?.balance || 0 : 0);
    });

    return balances;
  }

  /**
   * 判斷是否有兩個以上的 social login methods
   *
   * @param userThirdPartyInfoList
   */
  checkUserHasTwoSocialLoginMethods(userThirdPartyInfoList: SocialData[]): boolean {
    const filtered = userThirdPartyInfoList.filter(list => list.socialUserId && list.socialUserName);
    return filtered.length > 1 ? true : false;
  }

  readonly walletSummary$ = merge(this.manualWalletSummaryRefresh$, this.userBalanceRefresh$).pipe(
    switchMap(() =>
      from(this.userAssetsService.getWalletInfor()).pipe(
        switchMap(walletViewData => {
          if (!walletViewData) {
            return of({
              walletInfo: null,
              summary: {
                walletList: [],
                pubsWalletAsset: 0,
                lockBonus: 0,
                totalAsset: 0,
              },
            });
          }

          return forkJoin({
            bonus: this.newBonusWalletApi.getActiveBonusBalance(),
            newData: this.formatAndProcessWalletViewData(walletViewData, Config.walletDesc),
          }).pipe(
            map(({ bonus, newData }) => {
              const lockBonus = bonus.balance ?? 0;
              const totalAsset = newData.pubsWalletAsset + lockBonus;

              return {
                walletInfo: walletViewData,
                summary: {
                  ...newData,
                  lockBonus,
                  totalAsset,
                },
              };
            }),
          );
        }),
      ),
    ),
    shareReplay(1),
  );

  refreshWalletSummary(): void {
    this.manualWalletSummaryRefresh$.next();
  }
}
